import { Box, Drawer, Group, Stack, Text, Title } from "@mantine/core"
import { IconChevronLeft } from "@tabler/icons-react"
import type { PaginationState } from "@tanstack/react-table"
import { ActionIcon, AreYouSureModal } from "@tfg/ui"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import type { FindProductionSitesForSupplierResponseDto } from "~/gen/api"
import useContactForm from "~/pages/SupplierPortal/Contacts/hooks/useContactForm"
import { useProductionSiteFormErrorTranslations } from "~/pages/SupplierPortal/ProductionSites/hooks/use-production-site-form-error-translations"
import { useGetProductionSiteFormSchema } from "~/pages/SupplierPortal/ProductionSites/hooks/useProductionSiteFormSchema"
import {
  useGetProductionSiteFormTabs,
  type ProductionSiteFormValues,
} from "~/pages/SupplierPortal/ProductionSites/schemas/productionSiteForm.schema"
import { ContactFormPanel } from "~/pages/SupplierPortal/TraceabilityRequests/components/ContactFormPanel"
import { defaultUpdatePermissions } from "~/pages/Brand/Suppliers/SuppliersRepository/utils/useFormFieldConfig"
import { useAuth } from "~/store/auth-context"
import { createdBusinessPartnerSelectionFromResult } from "~/utils/zod/apply-created-business-partner-selection"
import {
  SchemaFormNestedViewProvider,
  type BusinessPartnerCreatedHandler,
} from "~/utils/zod/schema-form-nested-view-context"
import { SchemaForm } from "~/utils/zod/schema-form"
import {
  type CreatedProductionSiteSnapshot,
  type CreateProductionSiteResultKind,
  getCreateModeInitialValues,
  getEditModeInitialValues,
} from "./edit-production-site-drawer.helpers"
import { useProductionSiteDrawerMutations } from "./use-production-site-drawer-mutations"
import {
  getCanEditParentCompany,
  getParentCompanyForEdit,
  hasParentCompanySelectionChanged,
  resolveParentCompanyOrganizationId,
} from "../utils/productionSiteParentCompany.utils"

export type { CreatedProductionSiteSnapshot } from "./edit-production-site-drawer.helpers"

interface BaseProps {
  readonly opened: boolean
  readonly onClose: () => void
}

interface CreateModeProps extends BaseProps {
  readonly productionSite?: undefined
  readonly pagination: PaginationState
  readonly onProductionSiteCreated?: (
    productionSiteId: string,
    kind: CreateProductionSiteResultKind | undefined,
    createdSite?: CreatedProductionSiteSnapshot
  ) => void
}

interface EditModeProps extends BaseProps {
  readonly productionSite: FindProductionSitesForSupplierResponseDto
  readonly pagination: PaginationState
  readonly onProductionSiteCreated?: undefined
}

type Props = CreateModeProps | EditModeProps

function isEditMode(props: Props): props is EditModeProps & {
  productionSite: FindProductionSitesForSupplierResponseDto
} {
  return props.productionSite !== undefined
}

type ProductionSiteDrawerView = "productionSite" | "createBusinessPartner"

const DRAWER_VIEW_TRANSITION_MS = 220
export const EditProductionSiteDrawer = (props: Props) => {
  const { opened, onClose } = props
  const isEdit = isEditMode(props)
  const { t } = useTranslation(["supplier", "organizationSettings", "common"])
  const { user } = useAuth()
  const organizationId = user?.organization?.id ?? ""
  const productionSite = isEdit ? props.productionSite : undefined
  const [drawerView, setDrawerView] =
    useState<ProductionSiteDrawerView>("productionSite")
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [isParentCompanyChangeModalOpen, setIsParentCompanyChangeModalOpen] =
    useState(false)
  const [pendingSubmitValues, setPendingSubmitValues] =
    useState<ProductionSiteFormValues | null>(null)
  const [, setDuplicateCandidateId] = useState<string | null>(null)
  const lastCreateFormValuesRef = useRef<ProductionSiteFormValues | null>(null)
  const businessPartnerCreatedHandlerRef =
    useRef<BusinessPartnerCreatedHandler | null>(null)
  const onDuplicateCandidateForCreateNew = useCallback(
    (duplicateCandidateId: string | null): void => {
      setDuplicateCandidateId(duplicateCandidateId)
    },
    []
  )

  const showProductionSiteForm = drawerView === "productionSite"
  const showCreateBusinessPartnerForm = drawerView === "createBusinessPartner"

  const goBackToProductionSiteForm = useCallback((): void => {
    setDrawerView("productionSite")
  }, [])

  const handleDrawerClose = useCallback((): void => {
    if (showCreateBusinessPartnerForm) {
      goBackToProductionSiteForm()
      return
    }
    onClose()
  }, [goBackToProductionSiteForm, onClose, showCreateBusinessPartnerForm])

  useEffect(() => {
    if (!opened) {
      setDrawerView("productionSite")
    }
  }, [opened])

  const {
    contact,
    handleContact,
    reset: resetContactDraft,
  } = useContactForm({
    organizationId,
    onContactCreated: (result) => {
      const selection = createdBusinessPartnerSelectionFromResult(result)
      businessPartnerCreatedHandlerRef.current?.(selection)
      setDrawerView("productionSite")
    },
  })

  const nestedViewContextValue = useMemo(
    () => ({
      isBusinessPartnerCreateHosted: true,
      openCreateBusinessPartnerView: (): void => {
        resetContactDraft()
        setDrawerView("createBusinessPartner")
      },
      registerBusinessPartnerCreatedHandler: (
        handler: BusinessPartnerCreatedHandler
      ): (() => void) => {
        businessPartnerCreatedHandlerRef.current = handler
        return () => {
          if (businessPartnerCreatedHandlerRef.current === handler) {
            businessPartnerCreatedHandlerRef.current = null
          }
        }
      },
    }),
    [isEdit, resetContactDraft]
  )

  const {
    schema,
    isLoading: schemaLoading,
    businessRelationshipOptions,
  } = useGetProductionSiteFormSchema(
    isEdit,
    onDuplicateCandidateForCreateNew,
    getCanEditParentCompany(productionSite),
    getParentCompanyForEdit(productionSite)
  )

  const { deleteProductionSiteById, handleSubmit, isSubmitting } =
    useProductionSiteDrawerMutations({
      isEdit,
      onClose,
      pagination: props.pagination,
      productionSite,
      onProductionSiteCreated: props.onProductionSiteCreated,
      businessRelationshipOptions,
      lastCreateFormValuesRef,
    })

  const canEditParentCompany = getCanEditParentCompany(productionSite)
  const resolvedParentCompany =
    getParentCompanyForEdit(productionSite) ?? undefined

  const initialValues: Record<string, unknown> =
    isEdit && productionSite !== undefined
      ? getEditModeInitialValues(
          productionSite,
          canEditParentCompany,
          businessRelationshipOptions
        )
      : getCreateModeInitialValues()

  const productionSiteFormErrorTranslations =
    useProductionSiteFormErrorTranslations()
  const tabs = useGetProductionSiteFormTabs()
  const tabsWithTranslatedLabels = tabs.map((tab) => ({
    ...tab,
    label: (t as (key: string) => string)(tab.label),
  }))

  const productionSiteForm = (
    <SchemaForm
      deleteLabel={t("productionSiteModal.actions.delete")}
      handleDelete={() => setIsDeleteModalOpen(true)}
      schema={schema}
      initialValues={initialValues}
      onSubmit={handleFormSubmit}
      submitLabel={t("productionSiteModal.actions.save")}
      cancelLabel={t("productionSiteModal.actions.cancel")}
      onCancel={handleDrawerClose}
      isLoading={isSubmitting || schemaLoading}
      tabs={tabsWithTranslatedLabels}
      translateError={(error) =>
        productionSiteFormErrorTranslations[error] ?? error
      }
    />
  )
  function shouldConfirmParentCompanyChange(
    values: ProductionSiteFormValues
  ): boolean {
    if (
      !isEdit ||
      productionSite === undefined ||
      resolvedParentCompany === undefined
    ) {
      return false
    }

    if (!canEditParentCompany) {
      return false
    }

    const originalParentCompanyId = resolveParentCompanyOrganizationId(
      resolvedParentCompany,
      businessRelationshipOptions
    )

    return hasParentCompanySelectionChanged(
      values.parentCompany,
      originalParentCompanyId,
      businessRelationshipOptions
    )
  }

  function handleFormSubmit(values: ProductionSiteFormValues): void {
    if (shouldConfirmParentCompanyChange(values)) {
      setPendingSubmitValues(values)
      setIsParentCompanyChangeModalOpen(true)
      return
    }

    handleSubmit(values)
  }

  const drawerTitle = showCreateBusinessPartnerForm ? (
    <Stack gap={4}>
      <Group gap={4} wrap="nowrap">
        <ActionIcon
          icon={<IconChevronLeft />}
          onClick={goBackToProductionSiteForm}
          aria-label={t("productionSiteModal.actions.backToProductionSite")}
          variant="ghost"
        />
        <Text
          component="button"
          type="button"
          onClick={goBackToProductionSiteForm}
          size="sm"
          c="dimmed"
          style={{
            background: "none",
            border: 0,
            cursor: "pointer",
            padding: 0,
          }}
        >
          {t("productionSiteModal.actions.backToProductionSite")}
        </Text>
      </Group>
      <Title order={3}>
        {t("organizationSettings:supplierSettings.title")}
      </Title>
    </Stack>
  ) : (
    <Title order={3}>
      {isEdit
        ? t("productionSiteModal.editTitle")
        : t("productionSiteModal.createTitle")}
    </Title>
  )

  return (
    <Drawer
      opened={opened}
      position="right"
      onClose={handleDrawerClose}
      title={drawerTitle}
      styles={{
        body: {
          display: "flex",
          flex: "1 1 0%",
          flexDirection: "column",
          minHeight: "0",
          overflow: isEdit ? "auto" : "hidden",
        },
        content: {
          display: "flex",
          flexDirection: "column",
          height: "100%",
        },
      }}
      size="xl"
      h="100%"
    >
      <SchemaFormNestedViewProvider value={nestedViewContextValue}>
        {isEdit ? (
          showCreateBusinessPartnerForm ? (
            <ContactFormPanel
              isActive={showCreateBusinessPartnerForm}
              supplierMapping={contact}
              handleSupplierMapping={handleContact}
              isUpdate={false}
              updatePermissions={defaultUpdatePermissions}
              onCancel={goBackToProductionSiteForm}
              showAnswerOnBehalfPolicy
            />
          ) : (
            productionSiteForm
          )
        ) : (
          <Box
            style={{
              flex: 1,
              minHeight: 0,
              overflow: "hidden",
            }}
          >
            <Group
              wrap="nowrap"
              align="start"
              gap={0}
              w="200%"
              h="100%"
              style={{
                transform: showCreateBusinessPartnerForm
                  ? "translateX(-50%)"
                  : "translateX(0)",
                transition: `transform ${DRAWER_VIEW_TRANSITION_MS}ms ease`,
              }}
            >
              <Box
                aria-hidden={showCreateBusinessPartnerForm}
                style={{
                  flexShrink: 0,
                  overflow: "auto",
                  pointerEvents: showCreateBusinessPartnerForm
                    ? "none"
                    : undefined,
                  width: "50%",
                }}
              >
                {productionSiteForm}
              </Box>
              <Box
                aria-hidden={showProductionSiteForm}
                style={{
                  flexShrink: 0,
                  overflow: "auto",
                  pointerEvents: showProductionSiteForm ? "none" : undefined,
                  width: "50%",
                }}
              >
                <ContactFormPanel
                  isActive={showCreateBusinessPartnerForm}
                  supplierMapping={contact}
                  handleSupplierMapping={handleContact}
                  isUpdate={false}
                  updatePermissions={defaultUpdatePermissions}
                  onCancel={goBackToProductionSiteForm}
                  showAnswerOnBehalfPolicy
                />
              </Box>
            </Group>
          </Box>
        )}
      </SchemaFormNestedViewProvider>
      <AreYouSureModal
        opened={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={() => {
          if (productionSite) {
            deleteProductionSiteById(productionSite.id)
          }
          setIsDeleteModalOpen(false)
        }}
        areYouSureText={t("productionSiteModal.delete.message")}
        confirmText={t("productionSiteModal.delete.confirm")}
        cancelText={t("productionSiteModal.delete.cancel")}
        title={t("productionSiteModal.delete.title")}
      />
      <AreYouSureModal
        opened={isParentCompanyChangeModalOpen}
        onClose={() => {
          setIsParentCompanyChangeModalOpen(false)
          setPendingSubmitValues(null)
        }}
        onConfirm={() => {
          if (pendingSubmitValues !== null) {
            handleSubmit(pendingSubmitValues)
          }
          setIsParentCompanyChangeModalOpen(false)
          setPendingSubmitValues(null)
        }}
        areYouSureText={t(
          "productionSiteModal.parentCompanyChange.message",
          "The Parent Company is the company this site belongs to. Changing it will update ownership for this facility and may affect traceability routing."
        )}
        confirmText={t(
          "productionSiteModal.parentCompanyChange.confirm",
          "Change parent company"
        )}
        cancelText={t(
          "productionSiteModal.parentCompanyChange.cancel",
          "Keep current parent company"
        )}
        title={t(
          "productionSiteModal.parentCompanyChange.title",
          "Change parent company?"
        )}
      />
    </Drawer>
  )
}
